import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/news", label: "娱乐资讯" },
  { to: "/recommendations", label: "影视推荐" },
  { to: "/variety", label: "综艺动态" },
  { to: "/community", label: "互动专区" },
  { to: "/about", label: "关于我们" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
    setSearchOpen(false);
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    [
      "relative whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors",
      isActive
        ? "text-primary-600 font-semibold"
        : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/70",
    ].join(" ");

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="hidden bg-primary-600 text-background-50 md:block">
        <div className="flex w-full items-center justify-between gap-6 px-4 py-1.5 text-xs md:px-8">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="flex shrink-0 items-center gap-1 rounded-sm bg-background-50/15 px-1.5 py-0.5 font-label tracking-wide">
              <i className="ri-flashlight-fill text-accent-300" /> 今日热点
            </span>
            <Link to="/news/news-1" className="truncate text-background-50/90 hover:text-background-50">
              国庆档票房 42.6 亿收官，现实题材成最大赢家
            </Link>
          </div>
          <div className="hidden shrink-0 items-center gap-4 text-background-50/80 lg:flex">
            <span className="font-label">2026 年 10 月 17 日 星期六</span>
            <span className="h-3 w-px bg-background-50/30" />
            <span>红桃网 · 娱乐资讯与互动平台</span>
          </div>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:px-8">
          <Link to="/" className="group flex shrink-0 items-center gap-2.5" aria-label="红桃网首页">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 text-background-50 transition-colors group-hover:bg-primary-700">
              <i className="ri-heart-3-fill text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-black tracking-tight text-foreground-950">
                红桃网
              </span>
              <span className="mt-1 font-label text-[10px] uppercase tracking-[0.22em] text-foreground-400">
                全方位娱乐平台
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-0.5 lg:flex" aria-label="主导航">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={navClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden items-center md:flex">
              <div className="flex items-center gap-1.5 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 transition-colors focus-within:border-primary-400 focus-within:bg-background-50">
                <i className="ri-search-line text-base text-foreground-400" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索资讯 / 影视 / 综艺"
                  aria-label="站内搜索"
                  className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none xl:w-52"
                />
                <button
                  type="submit"
                  className="cursor-pointer rounded-full bg-primary-500 px-2.5 py-1 text-xs text-background-50 transition-colors hover:bg-primary-600"
                >
                  搜索
                </button>
              </div>
            </form>

            <button
              type="button"
              onClick={() => setSearchOpen((prev) => !prev)}
              aria-label="打开搜索"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
            >
              <i className="ri-search-line text-lg" />
            </button>

            <Link
              to="/community"
              className="hidden cursor-pointer items-center gap-1.5 rounded-full bg-foreground-950 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600 md:flex"
            >
              <i className="ri-chat-smile-2-line" />
              <span className="whitespace-nowrap">参与讨论</span>
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="打开菜单"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 lg:hidden"
            >
              <i className="ri-menu-line text-lg" />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="animate-fade-in border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
            <form onSubmit={submitSearch} className="flex items-center gap-2">
              <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
                <i className="ri-search-line text-base text-foreground-400" />
                <input
                  autoFocus
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索资讯 / 影视 / 综艺"
                  aria-label="站内搜索"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="shrink-0 cursor-pointer rounded-full bg-primary-500 px-4 py-2 text-sm text-background-50"
              >
                搜索
              </button>
            </form>
          </div>
        )}
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭菜单"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 cursor-pointer bg-foreground-950/45"
          />
          <div className="absolute right-0 top-0 flex h-full w-[302px] max-w-[86vw] flex-col bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <span className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-600 text-background-50">
                  <i className="ri-heart-3-fill" />
                </span>
                <span className="font-heading text-base font-bold">红桃网</span>
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="关闭菜单"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="移动端主导航">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    [
                      "flex items-center justify-between rounded-md px-3 py-3 text-sm transition-colors",
                      isActive
                        ? "bg-primary-50 font-semibold text-primary-600"
                        : "text-foreground-800 hover:bg-background-100",
                    ].join(" ")
                  }
                >
                  <span>{item.label}</span>
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </NavLink>
              ))}
            </nav>

            <div className="border-t border-background-200 px-5 py-4 text-xs text-foreground-500">
              <p className="mb-2 font-heading text-sm font-semibold text-foreground-900">红桃网</p>
              <p className="leading-relaxed">全方位娱乐资讯与互动平台，看见好内容，也聊得尽兴。</p>
              <div className="mt-3 flex items-center gap-3 text-lg text-foreground-500">
                <i className="ri-weibo-line" />
                <i className="ri-wechat-line" />
                <i className="ri-video-line" />
                <i className="ri-mail-line" />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}